import {
  Content,
  Header,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { spassuCodeApiRef } from '../api';
import type { ActivityWorkPath } from '../api/types';
import { controlPlaneCockpitRouteRef } from '../routes';

type EntryState =
  | { status: 'loading' }
  | { status: 'ready'; workPath: ActivityWorkPath | undefined }
  | { status: 'error'; error: Error };

const EntryProgress = () => (
  <Page themeId="tool">
    <Header title="Carregando cockpit" />
    <Content>
      <Progress />
    </Content>
  </Page>
);

const EntryError = ({ error }: { error: Error }) => (
  <Page themeId="tool">
    <Header title="Cockpit indisponível" />
    <Content>
      <ResponseErrorPanel error={error} />
    </Content>
  </Page>
);

/**
 * Historical `/spassu-code/metrics/:id` links. Control Plane is the only work
 * path with a cockpit, served by its dedicated plugin.
 */
export const ActivityCockpitRedirect = ({
  activityId,
}: {
  activityId: string;
}) => {
  const api = useApi(spassuCodeApiRef);
  const location = useLocation();
  const controlPlaneCockpitRoute = useRouteRef(controlPlaneCockpitRouteRef);
  const [state, setState] = useState<EntryState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    api
      .getActivity(activityId)
      .then(activity => {
        if (active) setState({ status: 'ready', workPath: activity.workPath });
      })
      .catch(error => {
        if (active)
          setState({
            status: 'error',
            error: error instanceof Error ? error : new Error(String(error)),
          });
      });
    return () => {
      active = false;
    };
  }, [activityId, api]);

  if (state.status === 'loading') return <EntryProgress />;
  if (state.status === 'error') return <EntryError error={state.error} />;
  if (state.workPath === 'control-plane' && controlPlaneCockpitRoute) {
    return (
      <Navigate
        replace
        to={{
          pathname: controlPlaneCockpitRoute({ activityId }),
          search: location.search,
        }}
      />
    );
  }
  return (
    <EntryError
      error={new Error('Não há cockpit compatível para a via da atividade.')}
    />
  );
};
